import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CreditCard,
  TrendingUp,
  Users,
  DollarSign,
  RefreshCw,
  Plus,
} from "lucide-react";
import { supabase } from "../integrations/supabase/client";

import { CreateSubscriptionModal } from "../components/CreateSubscriptionModal";

export function Subscriptions() {
  const navigate = useNavigate();
 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from("subscriptions")
        .select("*")
        .order("created_at", { ascending: false });
      setSubscriptions(data || []);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const planPrices: Record<string, number> = {
    starter: 149,
    pro: 349,
    business: 799,
    enterprise: 1999,
  };

  const mrr = subscriptions
    .filter((s) => s.status === "active")
    .reduce((acc, s) => acc + (planPrices[s.plan] || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Assinaturas</h1>
          <p className="text-slate-400">Gestão de planos e cobranças</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchSubscriptions}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-slate-300 hover:bg-white/10"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Atualizar
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 rounded-lg text-white font-medium hover:bg-red-600 transition-all"
          >
            <Plus className="w-4 h-4" />
            Nova Subscrição
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-red-500/20 to-orange-500/10 border border-red-500/20 rounded-2xl p-6">
          <DollarSign className="w-8 h-8 text-red-400 mb-3" />
          <p className="text-sm text-slate-400">MRR Total</p>
          <p className="text-3xl font-bold text-white">
            R$ {mrr.toLocaleString()}
          </p>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <Users className="w-8 h-8 text-emerald-400 mb-3" />
          <p className="text-sm text-slate-400">Assinaturas Ativas</p>
          <p className="text-3xl font-bold text-white">
            {subscriptions.filter((s) => s.status === "active").length}
          </p>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <CreditCard className="w-8 h-8 text-blue-400 mb-3" />
          <p className="text-sm text-slate-400">Em Trial</p>
          <p className="text-3xl font-bold text-white">
            {subscriptions.filter((s) => s.status === "trialing").length}
          </p>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <TrendingUp className="w-8 h-8 text-amber-400 mb-3" />
          <p className="text-sm text-slate-400">ARR Projetado</p>
          <p className="text-3xl font-bold text-white">
            R$ {(mrr * 12).toLocaleString()}
          </p>
        </div>
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">
                  ID
                </th>
                <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">
                  Plano
                </th>
                <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">
                  Status
                </th>
                <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">
                  Valor
                </th>
                <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">
                  Criado
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {subscriptions.map((sub) => (
                <tr
                  key={sub.id}
                  className="hover:bg-white/5 cursor-pointer"
                  onClick={() => navigate(`/clients/${sub.user_id}`)}
                >
                  <td className="py-4 px-6 text-white font-mono text-sm">
                    {sub.user_id?.slice(0, 8) || sub.id.slice(0, 8)}...
                  </td>
                  <td className="py-4 px-6">
                    <span className="px-2.5 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs capitalize">
                      {sub.plan}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs ${
                        sub.status === "active"
                          ? "bg-emerald-500/20 text-emerald-400"
                          : sub.status === "trialing"
                            ? "bg-blue-500/20 text-blue-400"
                            : "bg-red-500/20 text-red-400"
                      }`}
                    >
                      {sub.status === "active"
                        ? "Ativo"
                        : sub.status === "trialing"
                          ? "Trial"
                          : sub.status === "past_due"
                            ? "Pendente"
                            : sub.status === "canceled"
                              ? "Cancelado"
                              : sub.status}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-white">
                    R$ {planPrices[sub.plan] || 0}
                  </td>
                  <td className="py-4 px-6 text-slate-400 text-sm">
                    {new Date(sub.created_at).toLocaleDateString("pt-BR")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Subscription Modal */}
      <CreateSubscriptionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchSubscriptions}
      />
    </div>
  );
}
